import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { contentApi } from '../services/content-api';
import { categoryApi, type CreateCategoryInput } from '../services/category-api';
import { subcategoryApi } from '../services/subcategory-api';
import { contentKeys } from './useContent';
import { branchKeys } from './queryKeys';
import { contentComparisonKeys } from './useContentComparison';
// Spec 022 — the asset library's grid + sidebar read from a
// distinct query-key namespace (`['assets', …]` instead of
// `contentKeys`'s `['content', …]`). Every mutation that mutates
// category / subcategory / content shape needs to invalidate this
// too so the asset library reflects the change in real time. Older
// versions of every mutation here invalidated contentKeys but
// missed assetListKeys — the asset library's grid only refreshed
// on a manual page reload.
import { assetListKeys } from './useAssetList';

export interface PublishedContentParams {
  contentType?: string;
  section?: string;
  category?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export const categoryKeys = {
  all: ['categories'] as const,
  list: (section?: string, branchId?: string) =>
    [...categoryKeys.all, 'list', section, branchId] as const,
};

export const subcategoryKeys = {
  all: ['subcategories'] as const,
  byCategory: (categoryId: string, branchId?: string) =>
    [...subcategoryKeys.all, 'byCategory', categoryId, branchId] as const,
  byCategories: (categoryIds: string[], branchId?: string) =>
    [...subcategoryKeys.all, 'byCategories', branchId, ...categoryIds] as const,
};

/**
 * Fetch published content with filtering and pagination
 */
export function usePublishedContent(params: PublishedContentParams = {}) {
  const { contentType, section, category, search, page = 1, limit = 12 } = params;

  const filterKey = useMemo(
    () => ({
      contentType: contentType || undefined,
      section: section || undefined,
      category: category || undefined,
      search: search || undefined,
      page: String(page),
      limit: String(limit),
    }),
    [contentType, section, category, search, page, limit]
  );

  return useQuery({
    queryKey: contentKeys.published(filterKey as Record<string, string>),
    queryFn: () =>
      contentApi.listPublished({
        contentType: contentType || undefined,
        section: section || undefined,
        category: category || undefined,
        search: search || undefined,
        page,
        limit,
      }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Fetch a single published content by slug
 */
export function useContentBySlug(slug: string | undefined) {
  return useQuery({
    queryKey: contentKeys.publishedBySlug(slug ?? ''),
    queryFn: () => contentApi.getBySlug(slug!),
    enabled: !!slug,
    staleTime: 10 * 60 * 1000, // 10 minutes
    // Don't retry on 403 — feature 016 returns ACCESS_REQUIRED for content
    // the viewer can't access; the Library page renders an interstitial.
    retry: (count, err) => {
      const apiErr = err as { status?: number; code?: string };
      if (apiErr?.status === 403 || apiErr?.code === 'ACCESS_REQUIRED') return false;
      return count < 1;
    },
  });
}

/**
 * Fetch persistent categories from the API. When a branchId is supplied,
 * pending branch-local renames are returned in place of the global name.
 */
export function usePersistentCategories(section?: string, branchId?: string) {
  return useQuery({
    queryKey: categoryKeys.list(section, branchId),
    queryFn: () => categoryApi.list(section, branchId),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Derive unique categories from published content with counts,
 * merged with persistent (admin-created) categories
 */
export function useCategories(section?: string) {
  // Fetch all published content to derive categories
  const { data, isLoading: isLoadingContent } = usePublishedContent({ section, limit: 100 });

  // Fetch persistent categories from the API
  const { data: persistentCategories, isLoading: isLoadingPersistent } = usePersistentCategories(section);

  const items = data?.items;

  const { categories, categoryCounts } = useMemo(() => {
    const countMap: Record<string, number> = {};

    // Count categories from content items
    if (items) {
      items.forEach((item) => {
        if (item.category) {
          countMap[item.category] = (countMap[item.category] || 0) + 1;
        }
      });
    }

    // Merge persistent categories (even if count=0)
    if (persistentCategories) {
      persistentCategories.forEach((cat) => {
        if (!(cat.name in countMap)) {
          countMap[cat.name] = 0;
        }
      });
    }

    const sortedCategories = Object.keys(countMap).sort();
    return { categories: sortedCategories, categoryCounts: countMap };
  }, [items, persistentCategories]);

  return { categories, categoryCounts, isLoading: isLoadingContent || isLoadingPersistent };
}

/**
 * Mutation to create a persistent category
 */
export function useCreateCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateCategoryInput) => categoryApi.create(input),
    onSuccess: () => {
      // Invalidate every cached categories list (TanStack Query matches by
      // key prefix). The cache key is `['categories', 'list', section, branchId]`,
      // so a list with a non-null branchId — like the active branch sidebar —
      // wouldn't match a more-specific invalidation and would stay stale until
      // page refresh.
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      // Feature 016: a brand-new category is now also a changelog entry on
      // the active branch (via `created_in_branch_id`), so the comparison
      // stats need to refetch too.
      queryClient.invalidateQueries({ queryKey: contentComparisonKeys.all });
      // Spec 022 — asset library reads via assetListKeys (its own
      // namespace). Required so the asset-section sidebar / root
      // view reflects the new category in real time.
      queryClient.invalidateQueries({ queryKey: assetListKeys.all });
    },
  });
}

/**
 * Mutation to rename a category by name. When a branchId is supplied, the
 * rename is staged on that branch and applied to the global table on publish.
 */
export function useRenameCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { section: string; oldName: string; newName: string; branchId?: string }) =>
      categoryApi.rename(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      queryClient.invalidateQueries({ queryKey: contentKeys.all });
      queryClient.invalidateQueries({ queryKey: contentComparisonKeys.all });
      queryClient.invalidateQueries({ queryKey: assetListKeys.all });
    },
  });
}

/**
 * Mutation to reorder categories within a section
 */
export function useReorderCategories() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ section, order }: { section: string; order: string[] }) =>
      categoryApi.reorder(section, order),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      queryClient.invalidateQueries({ queryKey: assetListKeys.all });
    },
  });
}

/**
 * Mutation to delete a persistent category
 */
export function useDeleteCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => categoryApi.delete(id),
    onSuccess: () => {
      // Invalidate all category lists
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      queryClient.invalidateQueries({ queryKey: contentComparisonKeys.all });
      queryClient.invalidateQueries({ queryKey: assetListKeys.all });
    },
  });
}

/**
 * Fetch subcategories for all categories in a section.
 * Fetches subcategories for each category and merges results.
 */
export function useSubcategoriesForCategories(categoryIds: string[], branchId?: string) {
  return useQuery({
    queryKey: subcategoryKeys.byCategories(categoryIds, branchId),
    queryFn: async () => {
      if (categoryIds.length === 0) return [];
      const results = await Promise.all(
        categoryIds.map((id) => subcategoryApi.list(id, branchId))
      );
      return results.flat();
    },
    enabled: categoryIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Mutation to create a subcategory
 */
export function useCreateSubcategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { name: string; categoryId: string; branchId: string }) =>
      subcategoryApi.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subcategoryKeys.all });
      queryClient.invalidateQueries({ queryKey: contentKeys.all });
      queryClient.invalidateQueries({ queryKey: contentComparisonKeys.all });
      queryClient.invalidateQueries({ queryKey: assetListKeys.all });
    },
  });
}

/**
 * Mutation to rename a subcategory
 */
export function useRenameSubcategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, name, branchId }: { id: string; name: string; branchId: string }) =>
      subcategoryApi.rename(id, { name, branchId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subcategoryKeys.all });
      queryClient.invalidateQueries({ queryKey: contentComparisonKeys.all });
      queryClient.invalidateQueries({ queryKey: assetListKeys.all });
    },
  });
}

/**
 * Mutation to delete a subcategory (cascade-deletes content)
 */
export function useDeleteSubcategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, branchId }: { id: string; branchId: string }) =>
      subcategoryApi.delete(id, branchId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: subcategoryKeys.all });
      queryClient.invalidateQueries({ queryKey: contentKeys.all });
      queryClient.invalidateQueries({ queryKey: branchKeys.detail(variables.branchId) });
      queryClient.invalidateQueries({ queryKey: contentComparisonKeys.all });
      queryClient.invalidateQueries({ queryKey: assetListKeys.all });
    },
  });
}

/**
 * Mutation to reorder subcategories and loose content within a category
 */
export function useReorderSubcategories() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { categoryId: string; branchId: string; order: { type: 'subcategory' | 'content'; id: string }[] }) =>
      subcategoryApi.reorder(input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: subcategoryKeys.all });
      queryClient.invalidateQueries({ queryKey: contentKeys.all });
      queryClient.invalidateQueries({ queryKey: branchKeys.detail(variables.branchId) });
      queryClient.invalidateQueries({ queryKey: assetListKeys.all });
    },
  });
}

/**
 * Mutation to reorder content items within a subcategory
 */
export function useReorderSubcategoryContents() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ subcategoryId, ...input }: { subcategoryId: string; branchId: string; order: string[] }) =>
      subcategoryApi.reorderContents(subcategoryId, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: subcategoryKeys.all });
      queryClient.invalidateQueries({ queryKey: contentKeys.all });
      queryClient.invalidateQueries({ queryKey: branchKeys.detail(variables.branchId) });
      queryClient.invalidateQueries({ queryKey: assetListKeys.all });
    },
  });
}

/**
 * Mutation to move content between subcategories (DnD reassignment)
 */
export function useMoveContent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contentId, ...input }: { contentId: string; branchId: string; subcategoryId: string | null; displayOrder: number }) =>
      subcategoryApi.moveContent(contentId, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: subcategoryKeys.all });
      queryClient.invalidateQueries({ queryKey: contentKeys.all });
      queryClient.invalidateQueries({ queryKey: branchKeys.detail(variables.branchId) });
      queryClient.invalidateQueries({ queryKey: contentComparisonKeys.all });
      queryClient.invalidateQueries({ queryKey: assetListKeys.all });
    },
  });
}
